import { useLingui } from '@lingui/react/macro'
import { useGeckoNotifications } from 'features/notifications'
import {
  MIN_DRAW_WIDTH_PX,
  overlayPosToMs,
} from 'features/timeSeries/components/chartOverlays/overlayPointer'
import {
  getSeriesInfoFromUPlot,
  VisibleSensorInfo,
} from 'features/timeSeries/components/rulerTool/useRulerTool'
import { getSensorColor } from 'features/timeSeries/config/timeSeriesColors'
import type { LabelGroupId } from 'features/timeSeries/labels/labelGroups'
import type { ProcessDataLabel } from 'features/timeSeries/labels/processDataLabelsService'
import { useProcessDataLabels } from 'features/timeSeries/labels/useProcessDataLabels'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import uPlot from 'uplot'

import { applyEdgeResize, LabelRange, normalizeRange } from './labelGeometry'

function describeError(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

export type LabelPanelState =
  | { mode: 'create'; range: LabelRange }
  | { mode: 'edit'; label: ProcessDataLabel }

export interface LabelDraft extends LabelRange {
  /** Set once the drag ends, which is what opens the config panel. */
  isComplete: boolean
}

export type LabelEdgeTarget = { kind: 'draft' } | { kind: 'label'; id: string }

interface UseLabelToolProps {
  uPlotRef: React.RefObject<uPlot | null>
  siteID: string | undefined
  allSensorsToRender: Array<{ id: string; name: string }>
  customSensorColors?: Record<string, string>
  isActive: boolean
  setVisibleLabelIds: React.Dispatch<React.SetStateAction<string[]>>
}

export function useLabelTool({
  uPlotRef,
  siteID,
  allSensorsToRender,
  customSensorColors,
  isActive,
  setVisibleLabelIds,
}: UseLabelToolProps) {
  const { t } = useLingui()

  const { labels, isLoading, createLabel, updateLabel, deleteLabel } =
    useProcessDataLabels(siteID)
  const { errorNotification } = useGeckoNotifications()

  const [draft, setDraft] = useState<LabelDraft | null>(null)
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [resizingEdge, setResizingEdge] = useState<'start' | 'end' | null>(null)
  const [resizeTarget, setResizeTarget] = useState<LabelEdgeTarget | null>(null)
  /**
   * Live range of the persisted label under an edge drag. Held here rather than
   * written through `updateLabel` on every mousemove so a resize is one write,
   * on release.
   */
  const [resizePreview, setResizePreview] = useState<
    (LabelRange & { id: string }) | null
  >(null)
  const [isSaving, setIsSaving] = useState(false)

  const dragStartMsRef = useRef<number | null>(null)
  const dragStartPxRef = useRef<number | null>(null)
  const lastChartPensRef = useRef<VisibleSensorInfo[]>([])

  const editingLabel =
    editingLabelId !== null
      ? (labels.find((label) => label.id === editingLabelId) ?? null)
      : null

  const panel: LabelPanelState | null = useMemo(
    () =>
      draft?.isComplete
        ? {
            mode: 'create',
            range: { startMs: draft.startMs, endMs: draft.endMs },
          }
        : editingLabel
          ? { mode: 'edit', label: editingLabel }
          : null,
    [draft, editingLabel]
  )

  /** Canonical color — same formula as the ruler and point tools. */
  const getSensorColorById = useCallback(
    (sensorId: string): string => {
      if (customSensorColors?.[sensorId]) return customSensorColors[sensorId]
      const index = allSensorsToRender.findIndex(
        (sensor) => sensor.id === sensorId
      )
      return getSensorColor(index >= 0 ? index : 0)
    },
    [allSensorsToRender, customSensorColors]
  )

  const getChartPens = useCallback((): VisibleSensorInfo[] => {
    const u = uPlotRef.current
    if (!u) return lastChartPensRef.current

    const pens = getSeriesInfoFromUPlot(u)
      .filter((series) => series.show && !series.isRegression)
      .map((series) => ({
        sensorId: series.sensorId,
        name: series.name,
        color: getSensorColorById(series.sensorId),
      }))

    lastChartPensRef.current = pens
    return pens
  }, [getSensorColorById, uPlotRef])

  const clearDrag = useCallback(() => {
    setIsDragging(false)
    setResizingEdge(null)
    setResizeTarget(null)
    dragStartMsRef.current = null
    dragStartPxRef.current = null
  }, [])

  const cancelDraft = useCallback(() => {
    setDraft(null)
    clearDrag()
  }, [clearDrag])

  const closeEditor = useCallback(() => {
    setEditingLabelId(null)
  }, [])

  const openEditor = useCallback((labelId: string) => {
    setDraft(null)
    setEditingLabelId(labelId)
  }, [])

  // Leaving the tool abandons an in-progress draw. An open editor survives:
  // titles are clickable with the tool off, so the editor is not tool-scoped.
  useEffect(() => {
    if (isActive) return
    setDraft(null)
    clearDrag()
    setResizePreview(null)
  }, [isActive, clearDrag])

  const posToMs = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) =>
      overlayPosToMs(uPlotRef.current, e),
    [uPlotRef]
  )

  const handleOverlayMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.button !== 0) return
      // A panel is open on an unresolved draft/edit; resolve it before drawing.
      if (panel !== null) return

      const position = posToMs(e)
      if (!position) return

      dragStartMsRef.current = position.valueMs
      dragStartPxRef.current = position.pixelX
      setDraft({
        startMs: position.valueMs,
        endMs: position.valueMs,
        isComplete: false,
      })
      setIsDragging(true)
      setResizingEdge(null)
      setResizeTarget(null)
      e.preventDefault()
    },
    [panel, posToMs]
  )

  const handleEdgeMouseDown = useCallback(
    (target: LabelEdgeTarget, edge: 'start' | 'end', e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      setResizeTarget(target)
      setResizingEdge(edge)
      setIsDragging(true)
      if (target.kind === 'label') {
        const label = labels.find((entry) => entry.id === target.id)
        if (label) {
          setResizePreview({
            id: label.id,
            startMs: label.startMs,
            endMs: label.endMs,
          })
        }
      }
    },
    [labels]
  )

  const handleOverlayMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDragging) return
      const position = posToMs(e)
      if (!position) return

      if (resizingEdge && resizeTarget) {
        if (resizeTarget.kind === 'draft') {
          if (!draft) return
          const next = applyEdgeResize(draft, resizingEdge, position.valueMs)
          setDraft({ ...draft, startMs: next.startMs, endMs: next.endMs })
          if (next.edge !== resizingEdge) setResizingEdge(next.edge)
          return
        }

        if (!resizePreview) return
        const next = applyEdgeResize(
          resizePreview,
          resizingEdge,
          position.valueMs
        )
        setResizePreview({
          ...resizePreview,
          startMs: next.startMs,
          endMs: next.endMs,
        })
        if (next.edge !== resizingEdge) setResizingEdge(next.edge)
        return
      }

      const startMs = dragStartMsRef.current
      if (startMs === null) return
      setDraft({
        ...normalizeRange(startMs, position.valueMs),
        isComplete: false,
      })
    },
    [draft, isDragging, posToMs, resizePreview, resizingEdge, resizeTarget]
  )

  const commitResize = useCallback(() => {
    const preview = resizePreview
    setResizePreview(null)
    if (!preview) return

    const label = labels.find((entry) => entry.id === preview.id)
    if (!label) return
    if (label.startMs === preview.startMs && label.endMs === preview.endMs) {
      return
    }

    updateLabel({
      ...label,
      startMs: preview.startMs,
      endMs: preview.endMs,
    }).catch((error: unknown) => {
      errorNotification(
        t`Failed to save label range: ${{ value261_57: describeError(error) }}`
      )
    })
  }, [errorNotification, labels, resizePreview, updateLabel, t])

  const handleOverlayMouseUp = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDragging) return

      if (resizingEdge && resizeTarget) {
        if (resizeTarget.kind === 'label') commitResize()
        clearDrag()
        return
      }

      const position = posToMs(e)
      const startPx = dragStartPxRef.current
      const startMs = dragStartMsRef.current
      clearDrag()

      if (!position || startPx === null || startMs === null) {
        setDraft(null)
        return
      }

      if (Math.abs(position.pixelX - startPx) < MIN_DRAW_WIDTH_PX) {
        setDraft(null)
        return
      }

      setDraft({
        ...normalizeRange(startMs, position.valueMs),
        isComplete: true,
      })
    },
    [clearDrag, commitResize, isDragging, posToMs, resizeTarget, resizingEdge]
  )

  const handleOverlayMouseLeave = useCallback(() => {
    if (!isDragging) return

    // An edge drag is an explicit gesture on an existing label; leaving the
    // overlay mid-drag keeps it rather than silently reverting the range. A
    // half-drawn new region has nothing worth keeping.
    if (resizingEdge && resizeTarget?.kind === 'label') {
      commitResize()
    } else if (!resizingEdge) {
      setDraft(null)
    }
    clearDrag()
  }, [clearDrag, commitResize, isDragging, resizeTarget, resizingEdge])

  const saveDraft = useCallback(
    async (input: {
      name: string
      description: string
      groupId: LabelGroupId | null
      sensorIds: string[]
    }) => {
      if (!draft) return
      setIsSaving(true)
      try {
        const created = await createLabel({
          name: input.name,
          description: input.description,
          groupId: input.groupId,
          startMs: draft.startMs,
          endMs: draft.endMs,
          sensorIds: input.sensorIds,
        })
        setDraft(null)
        // A label you just drew stays on the chart; every other label is opt-in.
        setVisibleLabelIds((current) =>
          current.includes(created.id) ? current : [...current, created.id]
        )
      } catch (error) {
        // Keep the panel open on failure so the user's text is not lost.
        errorNotification(
          t`Failed to save label: ${{ value337_53: describeError(error) }}`
        )
      } finally {
        setIsSaving(false)
      }
    },
    [createLabel, draft, errorNotification, setVisibleLabelIds, t]
  )

  const saveEdits = useCallback(
    async (input: {
      name: string
      description: string
      groupId: LabelGroupId | null
      sensorIds: string[]
    }) => {
      if (!editingLabel) return
      setIsSaving(true)
      try {
        await updateLabel({ ...editingLabel, ...input })
        setEditingLabelId(null)
      } catch (error) {
        errorNotification(
          t`Failed to save label: ${{ value358_53: describeError(error) }}`
        )
      } finally {
        setIsSaving(false)
      }
    },
    [editingLabel, errorNotification, updateLabel, t]
  )

  const removeEditingLabel = useCallback(async () => {
    if (!editingLabel) return
    setIsSaving(true)
    try {
      await deleteLabel(editingLabel.id)
      setEditingLabelId(null)
      setVisibleLabelIds((current) =>
        current.filter((id) => id !== editingLabel.id)
      )
    } catch (error) {
      errorNotification(
        t`Failed to delete label: ${{ value376_53: describeError(error) }}`
      )
    } finally {
      setIsSaving(false)
    }
  }, [deleteLabel, editingLabel, errorNotification, setVisibleLabelIds, t])

  /**
   * Esc resolves the open panel (discarding, in both modes) instead of exiting
   * the tool. `TimeSeriesScatterplotContent` defers to this while a panel is
   * open; with none open its own handler exits the tool as usual.
   */
  useEffect(() => {
    if (panel === null) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (panel.mode === 'create') cancelDraft()
      else closeEditor()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [cancelDraft, closeEditor, panel])

  /** Range to draw for a label: the live edge drag if one is in flight. */
  const getRenderedRange = useCallback(
    (label: ProcessDataLabel): LabelRange =>
      resizePreview !== null && resizePreview.id === label.id
        ? { startMs: resizePreview.startMs, endMs: resizePreview.endMs }
        : { startMs: label.startMs, endMs: label.endMs },
    [resizePreview]
  )

  return {
    labels,
    isLoading,
    draft,
    panel,
    editingLabelId,
    isDragging,
    resizingEdge,
    isSaving,
    getChartPens,
    getRenderedRange,
    openEditor,
    closeEditor,
    cancelDraft,
    saveDraft,
    saveEdits,
    removeEditingLabel,
    handleOverlayMouseDown,
    handleOverlayMouseMove,
    handleOverlayMouseUp,
    handleOverlayMouseLeave,
    handleEdgeMouseDown,
  }
}
